<script setup lang="ts">
/**
 * One tooltip for the whole application, drawn at the top of the page.
 *
 * Tooltips used to be a CSS pseudo-element on the hovered control itself, which
 * is neat and cannot work. A child cannot be painted above a stacking context
 * it lives inside, so the prestige tooltip in the top bar went *behind* the
 * unit cards on the map, and anything inside a panel with `overflow: hidden`
 * was clipped at the panel's edge. Raising z-index on the trigger cannot fix
 * either: the ancestor decides.
 *
 * So the tooltip is not drawn by the trigger at all. One element lives at the
 * end of the body, above everything, and this component listens for hover
 * anywhere in the document and moves it. The markup that wants a tooltip is
 * unchanged: `data-tip="..."` on anything.
 *
 * Kept deliberately dumb: no delay, no interactivity, no HTML in the text. A
 * tooltip that can be hovered or clicked is a menu, and this is a label.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';

/** Distance from the trigger to the balloon, leaving room for the arrow. */
const GAP = 9;
/** Keep this clear of the window edges. */
const MARGIN = 8;

const text = ref('');
// Off-screen until placed. The balloon has to be laid out before it can be
// measured, and at 0,0 that one frame is a flicker in the corner of the page.
const x = ref(-9999);
const y = ref(-9999);
const below = ref(true);
/** Where the arrow sits along the balloon, once the balloon has been clamped. */
const arrowX = ref(0);
const shown = ref(false);

const balloon = ref<HTMLElement | null>(null);
let trigger: HTMLElement | null = null;

function hide(): void {
  shown.value = false;
  x.value = -9999;
  y.value = -9999;
  trigger = null;
}

/**
 * Place the balloon against the trigger.
 *
 * Two passes are unavoidable: the balloon has to be in the DOM with its text
 * before it can be measured, and it cannot be positioned before it is measured.
 * So it is shown off-screen for one frame, then moved.
 */
function place(): void {
  const el = trigger;
  const box = balloon.value;
  if (!el || !box) return;
  if (!el.isConnected) {
    hide();
    return;
  }

  const r = el.getBoundingClientRect();
  const w = box.offsetWidth;
  const h = box.offsetHeight;

  // Below by default, above when there is no room, which is what happens to
  // anything docked to the bottom of the screen.
  const roomBelow = window.innerHeight - r.bottom;
  below.value = roomBelow >= h + GAP + MARGIN || r.top < h + GAP + MARGIN;
  y.value = below.value ? r.bottom + GAP : r.top - GAP - h;

  const centre = r.left + r.width / 2;
  const clamped = Math.max(MARGIN, Math.min(window.innerWidth - MARGIN - w, centre - w / 2));
  x.value = clamped;
  // The arrow stays under the trigger even when the balloon has been pushed
  // sideways to stay on screen, which is the whole point of tracking it.
  arrowX.value = Math.max(10, Math.min(w - 10, centre - clamped));
}

function show(el: HTMLElement, tip: string): void {
  trigger = el;
  text.value = tip;
  shown.value = true;
  // Measure after the text has been rendered, not before.
  requestAnimationFrame(place);
}

function onOver(event: Event): void {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const el = target.closest<HTMLElement>('[data-tip]');
  if (!el) {
    if (trigger) hide();
    return;
  }
  const tip = el.getAttribute('data-tip');
  if (!tip) {
    hide();
    return;
  }
  if (el === trigger) return;
  show(el, tip);
}

function onOut(event: PointerEvent): void {
  if (!trigger) return;
  const to = event.relatedTarget;
  if (to instanceof Node && trigger.contains(to)) return;
  hide();
}

/**
 * Keyboard users get the same label.
 *
 * `focusin` rather than `focus` because focus does not bubble, and a tooltip
 * that only appears for a mouse is a tooltip half the people cannot read.
 */
function onFocus(event: FocusEvent): void {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const el = target.closest<HTMLElement>('[data-tip]');
  const tip = el?.getAttribute('data-tip');
  if (el && tip) show(el, tip);
  else hide();
}

onMounted(() => {
  document.addEventListener('pointerover', onOver, true);
  document.addEventListener('pointerout', onOut, true);
  document.addEventListener('focusin', onFocus, true);
  // A tooltip anchored to something that has moved is worse than none, and
  // chasing it every frame is not worth it for a label.
  window.addEventListener('scroll', hide, true);
  window.addEventListener('resize', hide);
  window.addEventListener('pointerdown', hide, true);
  window.addEventListener('wheel', hide, { capture: true, passive: true });
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerover', onOver, true);
  document.removeEventListener('pointerout', onOut, true);
  document.removeEventListener('focusin', onFocus, true);
  window.removeEventListener('scroll', hide, true);
  window.removeEventListener('resize', hide);
  window.removeEventListener('pointerdown', hide, true);
  window.removeEventListener('wheel', hide, true);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-show="shown"
      ref="balloon"
      class="tip"
      :class="below ? 'tip--below' : 'tip--above'"
      role="tooltip"
      :style="{ left: `${x}px`, top: `${y}px` }"
    >
      {{ text }}
      <span class="tip__arrow" :style="{ left: `${arrowX}px` }"></span>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
/*
 * 400 sits above every panel, drawer and dock in the game and below the two
 * things that must never be covered: a confirm dialog (200) is modal, but the
 * control it explains can carry a tooltip, so the tooltip has to win.
 */
.tip {
  position: fixed;
  z-index: 400;
  max-width: 260px;
  padding: 0.35rem 0.55rem;
  background: rgb(24 21 17 / 96%);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  box-shadow: 0 3px 10px rgb(0 0 0 / 55%);
  color: var(--parchment);
  font-size: 0.74rem;
  line-height: 1.45;
  white-space: normal;
  pointer-events: none;
}

.tip__arrow {
  position: absolute;
  width: 8px;
  height: 8px;
  background: rgb(24 21 17 / 96%);
  transform: translateX(-50%) rotate(45deg);
}

.tip--below .tip__arrow {
  top: -5px;
  border-left: 1px solid var(--border-strong);
  border-top: 1px solid var(--border-strong);
}

.tip--above .tip__arrow {
  bottom: -5px;
  border-right: 1px solid var(--border-strong);
  border-bottom: 1px solid var(--border-strong);
}
</style>
